import { type ReactNode, useState } from 'react'
import { ArrowUp, ArrowDown } from 'lucide-react'

export interface Column<T> {
  key: string
  header: string
  width: number
  /** Renders the cell. Return a string for plain text or a node for links and tags. */
  render: (row: T) => ReactNode
  sortable?: boolean
  align?: 'left' | 'right'
  /** First column is sticky and may wrap to two lines. */
  sticky?: boolean
}

export type SortDirection = 'asc' | 'desc' | null

interface DataTableProps<T> {
  columns: Column<T>[]
  rows: T[]
  rowKey: (row: T) => string
  sortKey?: string | null
  sortDir?: SortDirection
  onSort?: (key: string, dir: SortDirection) => void
  emptyMessage?: string
  maxHeight?: string
  /** Names the scrollable region for screen readers. */
  caption?: string
}

function SortIcon({ dir }: { dir: SortDirection }) {
  if (!dir) return null
  return dir === 'asc' ? <ArrowUp size={12} className="inline ml-1" /> : <ArrowDown size={12} className="inline ml-1" />
}

/**
 * Entity List table: explicit column widths, horizontal scroll, sticky first column and header,
 * 32px rows, no row hover (design system, Table Scroll Behavior).
 */
export function DataTable<T>({
  columns, rows, rowKey, sortKey, sortDir = null, onSort, emptyMessage = 'No records', maxHeight, caption,
}: DataTableProps<T>) {
  const total = columns.reduce((s, c) => s + c.width, 0)

  return (
    // tabIndex makes the scroll area reachable by keyboard (axe scrollable-region-focusable).
    <div
      className="overflow-auto bg-background-main"
      style={maxHeight ? { maxHeight } : undefined}
      tabIndex={0}
      role="region"
      aria-label={caption ?? 'Results table'}
    >
      <table style={{ width: total, borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <thead>
          <tr>
            {columns.map((c, i) => (
              <th
                key={c.key}
                className={`font-jakarta font-medium text-text-support bg-background-main sticky top-0 ${
                  i === 0 ? 'left-0 z-30' : 'z-20'
                }`}
                style={{
                  width: c.width, minHeight: 32, height: 32, padding: '8px 12px',
                  textAlign: c.align === 'right' ? 'right' : 'left', fontSize: 12, whiteSpace: 'normal',
                  boxShadow: 'inset 0 -0.5px 0 #4B5563', cursor: c.sortable && onSort ? 'pointer' : 'default',
                }}
                onClick={() => {
                  if (!c.sortable || !onSort) return
                  const next: SortDirection = sortKey === c.key ? (sortDir === 'asc' ? 'desc' : sortDir === 'desc' ? null : 'asc') : 'asc'
                  onSort(c.key, next)
                }}
              >
                {c.header}
                {sortKey === c.key && <SortIcon dir={sortDir} />}
                {i === 0 && (
                  <span style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: '0.5px', background: '#4B5563' }} />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="font-mulish text-text-support" style={{ padding: '20px 12px', fontSize: 12 }}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={rowKey(row)}>
                {columns.map((c, i) => (
                  <td
                    key={c.key}
                    className={`font-mulish text-white ${i === 0 ? 'sticky left-0 z-10 bg-background-main' : ''}`}
                    style={{
                      width: c.width, maxWidth: c.width, paddingTop: 8, paddingBottom: 8, paddingLeft: 12, paddingRight: 12,
                      lineHeight: '16px', fontSize: 12, borderBottom: '0.5px solid #4B5563',
                      textAlign: c.align === 'right' ? 'right' : 'left',
                      whiteSpace: i === 0 ? 'normal' : 'nowrap',
                      overflow: i === 0 ? undefined : 'hidden',
                      textOverflow: i === 0 ? undefined : 'ellipsis',
                      fontVariantNumeric: c.align === 'right' ? 'tabular-nums' : undefined,
                    }}
                  >
                    {c.render(row)}
                    {i === 0 && (
                      <span style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: '0.5px', background: '#4B5563' }} />
                    )}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}

/** Hyperlink-styled cell content. Always underlines on hover (global rule). */
export function LinkCell({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  return (
    <span className="text-text-hyperlink hover:underline cursor-pointer" onClick={onClick}>
      {children}
    </span>
  )
}

export function formatNumber(v: number | null | undefined): string {
  if (v === null || v === undefined) return '-'
  return v.toLocaleString('en-GB')
}

/** Monetary figures are always shown in full, never abbreviated (design system global rule). */
export function formatMoney(amount: number | null | undefined, currency = 'USD'): string {
  if (amount === null || amount === undefined) return '-'
  return `${currency} ${amount.toLocaleString('en-GB', { maximumFractionDigits: 2 })}`
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '-'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

export function useTableSort(initialKey?: string) {
  const [sortKey, setSortKey] = useState<string | null>(initialKey ?? null)
  const [sortDir, setSortDir] = useState<SortDirection>(initialKey ? 'asc' : null)
  const onSort = (key: string, dir: SortDirection) => {
    setSortKey(dir === null ? null : key)
    setSortDir(dir)
  }
  return { sortKey, sortDir, onSort }
}
